import linkIconUrl from '../../assets/svg/UI/link-white.svg';
import config from '../../config';

import dateRangeSelectorComponentUrl from './dateRangeSelectorComponent.html';

class DateRangeSelectorCtrl {
  constructor(Authentication, Utils, Education, sdk, $timeout, $scope, dateFilter) {
    'ngInject';

    Object.assign(this, { Authentication, Utils, Education, sdk, $timeout, $scope, dateFilter });
    this._initialized = true; // This is a guard for some weird behavior in unit tests
  }

  $onInit() {
    const { PAYROLL_PERIOD_TYPES } = this.sdk('constants');
    const payrollPeriodType =
      PAYROLL_PERIOD_TYPES[this.Authentication.user.orgSettings.payrollPeriodType];
    this.modes = [
      { label: 'Day', value: 'day' },
      { label: 'Week', value: 'week' },
      { label: 'Month', value: 'month' },
      { label: `Payroll (${payrollPeriodType})`, value: 'payroll' },
      { label: 'Quarter', value: 'quarter' },
      { label: 'Year', value: 'year' },
      { label: 'Custom', value: 'custom' },
    ];

    this._timeout = null;
    if (this.hideModes) {
      this.modes = this.modes.filter((m) => !this.hideModes.includes(m.value));
    }

    this.setRangeAndRefresh();
    this.displayDate = this.date;
    this.payrollDatepickerOptions = {
      payrollRangePicker: payrollPeriodType,
      payrollRangeStartOne: this.Authentication.user.orgSettings.payrollPeriodStartDate,
      payrollRangeStartTwo: this.Authentication.user.orgSettings.payrollPeriodStartDateTwo,
    };

    if (payrollPeriodType === PAYROLL_PERIOD_TYPES.MONTHLY) {
      this.payrollDatepickerOptions.minMode = 'month';
    }

    this.bookmarkForCurrentWeek = `${config.SLING_APP_URL}/shifts`;
    this.linkIconUrl = linkIconUrl;
    this.isTodaySelectedOut = this.isTodaySelected;
  }

  toggleCustomDatepicker() {
    this.datepickerOpened = !this.datepickerOpened;
  }

  _getCustomDisplayHeaderDates() {
    const start = this.displayRange[0];
    const end = this.displayRange[this.displayRange.length - 1];

    if (start === end) {
      return this.dateFilter(start, 'dd MMM');
    }

    const monthStart = this.dateFilter(start, 'MMM');
    const monthEnd = this.dateFilter(end, 'MMM');

    if (monthStart !== monthEnd) {
      return `${this.dateFilter(start, 'dd MMM')} - ${this.dateFilter(end, 'dd MMM')}`;
    }

    return `${this.dateFilter(start, 'dd')} - ${this.dateFilter(end, 'dd MMM')}`;
  }

  // eslint-disable-next-line complexity
  getDisplayHeaderDates() {
    switch (this.mode) {
      case 'day':
        return `${this.dateFilter(this.displayDate, 'EEE')} • ${this.dateFilter(
          this.displayDate,
          'dd MMM'
        )}`;
      case 'week':
      case 'week-2':
      case 'week-3':
      case 'week-4':
      case 'custom':
      case 'payroll':
        return this._getCustomDisplayHeaderDates();
      case 'month':
        return this.dateFilter(this.displayDate, 'MMMM yyyy');
      case 'quarter': {
        const dayMonth = this.displayDate.getMonth();
        const months = [-2, -1, 0, 1, 2].slice(2 - (dayMonth % 3), 5 - (dayMonth % 3));
        const from = this.Utils.addMonths(this.displayDate, months[0]);
        const to = this.Utils.addMonths(this.displayDate, months[months.length - 1]);
        return `${this.dateFilter(from, 'MMM')} - ${this.dateFilter(to, 'MMM')}`;
      }
      case 'year':
        return this.dateFilter(this.displayDate, 'yyyy');
      default:
        return 'Unknown Mode';
    }
  }

  get date() {
    return this._date;
  }

  set date(val) {
    if (!this._initialized) {
      return;
    }

    this._date = val;
    this.displayDate = val;

    if (this.mode === 'custom' && !this.displayRange && this.Utils) {
      this.customStart = this.Utils.getRealDate(this.date);
      this.customEnd = this.Utils.getRealDate(this.date);
    }

    if (!this.displayRange.includes(this.Utils.formatDate(val))) {
      this.setRangeAndRefresh();
    } else {
      this._range = this.displayRange;
    }

    this.isTodaySelectedOut = this.isTodaySelected;
  }

  get mode() {
    return this._mode;
  }

  set mode(val) {
    this._mode = val;

    if (val === 'custom' && this.displayRange && this.Utils) {
      const range = this.displayRange;
      this.customStart = this.Utils.getRealDate(range[0]);
      this.customEnd = this.Utils.getRealDate(range[range.length - 1]);
    }

    if (val !== 'custom' || (this.customStart && this.customEnd)) {
      this.setRangeAndRefresh();
    }
  }

  get customStart() {
    return this._customStart;
  }

  set customStart(val) {
    this._customStart = val;
    this.setDateAndRangeForCustomSelection();
  }

  get customEnd() {
    return this._customEnd;
  }

  set customEnd(val) {
    this._customEnd = val;
    this.setDateAndRangeForCustomSelection();
  }

  setDateAndRangeForCustomSelection() {
    if (this.customStart && this.customEnd && this.customStart <= this.customEnd) {
      if (
        this.date &&
        this.Utils.formatDate(this.date) !== this.Utils.formatDate(this.customStart)
      ) {
        this.displayDate = this.customStart;
        this._syncDateWithView();
      }

      this.setRangeAndRefresh();
    }
  }

  get range() {
    return this._range;
  }

  set range(value) {
    if (
      Array.isArray(value) &&
      (!this.customStart ||
        this.Utils.formatDate(this.customStart) !== value[0] ||
        !this.customEnd ||
        this.Utils.formatDate(this.customEnd) !== value[value.length - 1])
    ) {
      this._customStart = this.Utils.getRealDate(value[0]);
      this._customEnd = this.Utils.getRealDate(value[value.length - 1]);
      this.setDateAndRangeForCustomSelection();
    }

    this._range = value;
    this.displayRange = value;
  }

  _setWeekRange() {
    switch (this.mode) {
      case 'week-4': {
        const threeWeeksFromNow = this.Utils.addDays(this.displayDate, 21);
        this.displayRange = [
          ...this.Utils.getDaysForPeriod('week', threeWeeksFromNow),
          ...this.displayRange,
        ];
        this.numberOfWeeks++;
      }
      // eslint-disable-next-line no-fallthrough
      case 'week-3': {
        const twoWeeksFromNow = this.Utils.addDays(this.displayDate, 14);
        this.displayRange = [
          ...this.Utils.getDaysForPeriod('week', twoWeeksFromNow),
          ...this.displayRange,
        ];
        this.numberOfWeeks++;
      }
      // eslint-disable-next-line no-fallthrough
      case 'week-2': {
        const oneWeekFromNow = this.Utils.addDays(this.displayDate, 7);
        this.displayRange = [
          ...this.Utils.getDaysForPeriod('week', oneWeekFromNow),
          ...this.displayRange,
        ];
        this.numberOfWeeks++;
      }
      case 'week': // eslint-disable-line no-fallthrough
        this.displayRange = [
          ...this.Utils.getDaysForPeriod('week', this.displayDate),
          ...this.displayRange,
        ];
        this.numberOfWeeks++;
        return;
      default:
        throw 'not a week range';
    }
  }

  setRangeAndRefresh() {
    this.setRange();
    this._range = this.displayRange;
  }

  // eslint-disable-next-line complexity
  setRange() {
    this.displayRange = [];
    this.numberOfWeeks = 0;

    // for those who have set payroll range when it was visible
    if (this.hideModes?.includes(this.mode)) {
      this.mode = this.modes[0].value;
    }

    if (!this.displayDate) {
      return;
    }

    if (this.mode.startsWith('week')) {
      this._setWeekRange();
      return;
    }

    switch (this.mode) {
      case 'day':
        this.displayRange = [this.dateFilter(this.displayDate, 'yyyy-MM-dd')];
        break;
      case 'month':
      case 'quarter':
      case 'year':
      case 'payroll':
        this.displayRange = this.Utils.getDaysForPeriod(this.mode, this.displayDate);
        break;
      case 'month-horizontal':
        this.displayRange = this.Utils.getDaysForPeriod('month', this.displayDate);
        break;
      case 'custom': {
        this.displayRange = this.Utils.getDatesBetween(this.customStart, this.customEnd).map(
          this.Utils.formatDate
        );
        break;
      }
      default:
        console.warn('INVALID MODE', this.mode);
        return;
    }
  }

  async _syncDateWithView() {
    this.setRange();
    try {
      if (this._timeout) {
        await this._timeout;
      }

      this.date = this.displayDate;
      this.$scope.$evalAsync();
    } catch (e) {
      //
    }
  }

  jumpForward() {
    this.addJumpTimeout();
    this.jump();
  }

  jumpBackward() {
    this.addJumpTimeout();
    this.jump(true);
  }

  async addJumpTimeout() {
    if (this._timeout) {
      this.$timeout.cancel(this._timeout);
    }

    this._timeout = this.$timeout(() => {}, 300);

    try {
      await this._timeout;
      this._timeout = null;
    } catch (e) {
      //
    }
  }

  jump(backward = false) {
    if (this.mode === 'custom') {
      const modifier = backward ? -1 : 1;
      const prevStart = this.customStart;
      const prevEnd = this.customEnd;
      const prevRangeLength = this.displayRange.length;

      this.customStart = null;
      this.customEnd = null;
      this.customStart = this.Utils.addDays(prevStart, modifier * prevRangeLength);
      this.customEnd = this.Utils.addDays(prevEnd, modifier * prevRangeLength);
      return;
    }

    if (backward) {
      this.displayDate = this.Utils.addDays(this.displayRange[0], -1);
    } else {
      this.displayDate = this.Utils.addDays(this.displayRange[this.displayRange.length - 1], 1);
    }

    this._syncDateWithView();
  }

  get isTodaySelected() {
    return this.range?.includes(this.Utils.formatDate(this.Utils.getNowDate()));
  }

  jumpToToday(hideEducation) {
    if (hideEducation) {
      this.Education.hide();
    }

    if (!this.isTodaySelected) {
      const today = this.Utils.getNowDate();
      if (this.mode === 'custom') {
        this.range = [this.Utils.formatDate(today)];
      }

      this.date = today;
    }
  }
}

export default {
  templateUrl: dateRangeSelectorComponentUrl,
  controller: DateRangeSelectorCtrl,
  controllerAs: 'ctrl',
  bindings: {
    mode: '=',
    date: '=',
    range: '=?',
    datepickerOpened: '=?opened',
    hideModeSelector: '<?',
    hideModes: '<?',
    premiumModes: '<?',
    todayButton: '<',
    isTodaySelectedOut: '=?isTodaySelected',
  },
};
